import storefrontClient from "../client";
/**
 * Fetch a list of blog articles from Shopify, including SEO data
 * @param blogHandle The handle of the blog (e.g., "health")
 * @param limit Number of articles to fetch (default: 10)
 * @returns Promise with articles data
 */
export const fetchShopifyBlogArticles = async (
  blogHandle: string,
  limit = 10,
) => {
  try {
    const { data } = await storefrontClient.request(
      `
      query getBlogArticles($blogHandle: String!, $limit: Int!) {
        blog(handle: $blogHandle) {
          articles(first: $limit) {
            edges {
              node {
                id
                handle
                title
                content
                publishedAt
                image {
                  url
                  altText
                }
                seo {
                  title
                  description
                }
              }
            }
          }
        }
      }
    `,
      {
        variables: {
          blogHandle,
          limit,
        },
      },
    );
    return data.blog?.articles?.edges?.map((edge: any) => edge.node) || [];
  } catch (error) {
    console.error(
      `Error fetching blog articles from Shopify for blog "${blogHandle}":`,
      error,
    );
    throw error;
  }
};
/**
 * Fetch a single blog article by handle
 * @param blogHandle The handle of the blog (e.g., "health")
 * @param articleHandle The handle of the article
 * @returns Promise with article data
 */
export const fetchShopifyArticleByHandle = async (
  blogHandle: string,
  articleHandle: string,
) => {
  try {
    const { data } = await storefrontClient.request(
      `
      query getArticleByHandle($blogHandle: String!, $articleHandle: String!) {
        blog(handle: $blogHandle) {
          articleByHandle(handle: $articleHandle) {
            id
            title
            contentHtml
            publishedAt
            image {
              url
              altText
            }
              seo {
                  title
                  description
                }
          }
        }
      }
    `,
      {
        variables: {
          blogHandle,
          articleHandle,
        },
      },
    );
    return data?.blog?.articleByHandle;
  } catch (error) {
    console.error(
      `Error fetching article "${articleHandle}" from blog "${blogHandle}":`,
      error,
    );
    throw error;
  }
};
/**
 * Fetch trending blog articles from Shopify
 * @param blogHandle The handle of the blog (e.g., "health")
 * @returns Promise with trending articles data
 */
export const fetchTrendingBlogArticles = async (blogHandle: string) => {
  try {
    const { data } = await storefrontClient.request(
      `
      query getTrendingArticles($handle: String!) {
        blog(handle: $handle) {
          articles(first: 5, query: "tag:trending-this-week") {
            edges {
              node {
                id
                title
                handle
                publishedAt
                tags
                excerpt
                image {
                  url
                  altText
                }
              }
            }
          }
        }
      }
      `,
      {
        variables: {
          handle: blogHandle,
        },
      },
    );
    return data?.blog?.articles?.edges?.map((edge: any) => edge.node) || [];
  } catch (error) {
    console.error("Error fetching trending blog articles:", error);
    return [];
  }
};

/**
 * Fetch SEO metadata for a specific Shopify blog page
 * @param blogHandle The handle of the blog (e.g., "health", "grooming-tips")
 * @returns Promise with blog SEO title and description
 */
export const fetchShopifyBlogSeo = async (blogHandle: string) => {
  try {
    const { data } = await storefrontClient.request(
      `
      query getBlogSeo($blogHandle: String!) {
        blog(handle: $blogHandle) {
          title
          seo {
            title
            description
          }
        }
      }
      `,
      {
        variables: { blogHandle },
      },
    );

    return {
      title: data.blog?.title || null,
      seo: data.blog?.seo || null,
    };
  } catch (error) {
    console.error(
      `Error fetching SEO for Shopify blog "${blogHandle}":`,
      error,
    );
    throw error;
  }
};
